import { movies, type MediaType, type Movie } from "@/mocks/movies";
import { articles, type Article, type ArticleCategory } from "@/mocks/news";

export { movies, articles };
export type { Movie, MediaType, Article, ArticleCategory };

export type SortKey = "heat" | "newest" | "rating";

export interface MovieFilter {
  type?: MediaType;
  region?: string;
  year?: string;
  genre?: string;
  minRating?: number;
  keyword?: string;
  sort?: SortKey;
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((item) => item.id === id);
}

export function getMoviesByType(type: MediaType): Movie[] {
  return movies.filter((item) => item.type === type);
}

export function filterMovies(filter: MovieFilter): Movie[] {
  const { type, region, year, genre, minRating = 0, keyword, sort = "heat" } = filter;
  const normalizedKeyword = keyword?.trim().toLowerCase() ?? "";

  const result = movies.filter((item) => {
    if (type && item.type !== type) return false;
    if (region && region !== "全部地区" && item.region !== region) return false;
    if (year && year !== "全部年份" && String(item.year) !== year) return false;
    if (genre && genre !== "全部类型" && !item.genres.includes(genre)) return false;
    if (item.rating < minRating) return false;
    if (normalizedKeyword) {
      const haystack = [item.title, item.director, item.region, ...item.genres, ...item.cast, ...item.tags]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(normalizedKeyword)) return false;
    }
    return true;
  });

  return result.sort((a, b) => {
    if (sort === "rating") return b.rating - a.rating;
    if (sort === "newest") return b.year - a.year || b.heat - a.heat;
    return b.heat - a.heat;
  });
}

export function getRelatedMovies(movie: Movie, limit = 6): Movie[] {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      let score = 0;
      if (item.type === movie.type) score += 3;
      if (item.director === movie.director) score += 4;
      if (item.region === movie.region) score += 1;
      score += item.genres.filter((genre) => movie.genres.includes(genre)).length * 2;
      return { item, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || b.item.heat - a.item.heat);

  return scored.slice(0, limit).map(({ item }) => item);
}

export function getHotMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export function getLatestMovies(limit = 6): Movie[] {
  return [...movies].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, limit);
}

export function getArticleById(id: string): Article | undefined {
  return articles.find((item) => item.id === id);
}

export function filterArticles(category: string, keyword?: string): Article[] {
  const normalizedKeyword = keyword?.trim().toLowerCase() ?? "";
  return articles
    .filter((item) => (category === "全部" ? true : item.category === category))
    .filter((item) => {
      if (!normalizedKeyword) return true;
      const haystack = [item.title, item.summary, item.author, ...item.tags].join(" ").toLowerCase();
      return haystack.includes(normalizedKeyword);
    })
    .sort((a, b) => b.date.localeCompare(a.date));
}

export function getRelatedArticles(article: Article, limit = 4): Article[] {
  return articles
    .filter((item) => item.id !== article.id)
    .map((item) => {
      let score = item.category === article.category ? 2 : 0;
      score += item.tags.filter((tag) => article.tags.includes(tag)).length * 3;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.date.localeCompare(a.item.date))
    .slice(0, limit)
    .map(({ item }) => item);
}

export function searchMovies(keyword: string): Movie[] {
  const normalizedKeyword = keyword.trim().toLowerCase();
  if (!normalizedKeyword) return [];
  return movies.filter((item) => {
    const haystack = [item.title, item.director, item.region, ...item.genres, ...item.cast, ...item.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(normalizedKeyword);
  });
}

export function searchArticles(keyword: string): Article[] {
  const normalizedKeyword = keyword.trim().toLowerCase();
  if (!normalizedKeyword) return [];
  return articles.filter((item) => {
    const haystack = [item.title, item.summary, item.author, ...item.tags].join(" ").toLowerCase();
    return haystack.includes(normalizedKeyword);
  });
}

export function paginate<T>(items: T[], page: number, pageSize: number): T[] {
  const safePage = Math.max(1, page);
  return items.slice((safePage - 1) * pageSize, safePage * pageSize);
}

export function totalPages(count: number, pageSize: number): number {
  return Math.max(1, Math.ceil(count / pageSize));
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return String(views);
}